import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Landmark, Plus, Trash2, AlertTriangle } from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { useRole } from "@/lib/auth/use-role";
import {
  Badge,
  Button,
  Card,
  Dot,
  EmptyState,
  Field,
  PageHeader,
  Spinner,
} from "@/components/ui/primitives";
import {
  createContribution,
  deleteContribution,
  listContributions,
  setContributionStatus,
  type ContributionStatus,
} from "@/lib/api/conduct";
import { assessPayToPlay, DE_MINIMIS_NON_VOTING, DE_MINIMIS_VOTING } from "@argus/engine";

const STATUS_TONE: Record<ContributionStatus, "neutral" | "info" | "clear" | "breach" | "risk"> = {
  logged: "neutral",
  under_review: "info",
  cleared: "clear",
  flagged: "breach",
  remediated: "risk",
};

const STATUSES: ContributionStatus[] = [
  "logged",
  "under_review",
  "cleared",
  "flagged",
  "remediated",
];

function fmtUsd(n: number) {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function ContributionsInner() {
  const tenant = useTenant();
  const qc = useQueryClient();
  const { canWrite, role } = useRole(tenant.id);
  const canEdit = canWrite || role === "reviewer";
  const [adding, setAdding] = useState(false);

  const { data: rows, isLoading } = useQuery({
    queryKey: ["contributions", tenant.id],
    queryFn: () => listContributions(tenant.id),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["contributions", tenant.id] });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ContributionStatus }) =>
      setContributionStatus(id, status),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: deleteContribution, onSuccess: invalidate });

  const stats = useMemo(() => {
    const list = rows ?? [];
    const exceeds = list.filter(
      (r) => assessPayToPlay(r.amount_usd, r.can_vote_for_recipient).exceeds,
    );
    return {
      total: list.length,
      exceeds: exceeds.length,
      review: list.filter((r) => r.status === "under_review").length,
      flagged: list.filter((r) => r.status === "flagged").length,
    };
  }, [rows]);

  return (
    <>
      <PageHeader
        title="Political contributions"
        subtitle={`Pay-to-play (Rule 206(4)-5) look-back log. De minimis: ${fmtUsd(DE_MINIMIS_VOTING)} where the associate can vote for the recipient, ${fmtUsd(DE_MINIMIS_NON_VOTING)} otherwise.`}
        actions={
          canEdit ? (
            <Button variant="primary" onClick={() => setAdding((a) => !a)}>
              <Plus className="size-4" /> Log contribution
            </Button>
          ) : undefined
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: "Logged", value: stats.total, tone: "neutral" as const },
          { label: "Over de minimis", value: stats.exceeds, tone: "breach" as const },
          { label: "Under review", value: stats.review, tone: "info" as const },
          { label: "Flagged", value: stats.flagged, tone: "risk" as const },
        ].map((s) => (
          <Card key={s.label} className="px-4 py-3">
            <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-neutral-500">
              <Dot tone={s.tone} /> {s.label}
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums text-neutral-50">
              {s.value}
            </div>
          </Card>
        ))}
      </div>

      {adding && canEdit && (
        <AddContributionForm
          tenantId={tenant.id}
          onDone={() => {
            invalidate();
            setAdding(false);
          }}
        />
      )}

      {isLoading ? (
        <p className="text-sm text-neutral-500">Loading…</p>
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          icon={<Landmark className="size-7" />}
          title="No contributions logged"
          description="Log covered-associate political contributions to screen them against the de minimis limits and the two-year look-back ban."
          action={
            canEdit ? (
              <Button variant="primary" onClick={() => setAdding(true)}>
                <Plus className="size-4" /> Log contribution
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-neutral-800 text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Contributor</th>
                <th className="px-3 py-2 font-medium">Recipient</th>
                <th className="px-3 py-2 text-right font-medium">Amount</th>
                <th className="px-3 py-2 font-medium">Screen</th>
                <th className="px-3 py-2 font-medium">Status</th>
                {canEdit && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-900">
              {rows.map((r) => {
                const a = assessPayToPlay(r.amount_usd, r.can_vote_for_recipient);
                return (
                  <tr key={r.id} className="hover:bg-neutral-900/40">
                    <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-neutral-400">
                      {r.contribution_date}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="text-neutral-200">{r.contributor_name}</div>
                      {r.government_entity && (
                        <div className="text-[11px] text-neutral-500">
                          gov&rsquo;t client: {r.government_entity}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="text-neutral-200">{r.recipient_name}</div>
                      <div className="text-[11px] text-neutral-500">
                        {r.office_sought ?? "—"}
                        {r.jurisdiction ? ` · ${r.jurisdiction}` : ""}
                        {r.can_vote_for_recipient ? " · can vote" : ""}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-neutral-200">
                      {fmtUsd(r.amount_usd)}
                    </td>
                    <td className="px-3 py-2.5">
                      {a.exceeds ? (
                        <Badge tone="breach">
                          <AlertTriangle className="size-3" /> Over {fmtUsd(a.limit)}
                        </Badge>
                      ) : (
                        <Badge tone="clear">Within {fmtUsd(a.limit)}</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {canEdit ? (
                        <select
                          value={r.status}
                          onChange={(e) =>
                            setStatus.mutate({
                              id: r.id,
                              status: e.target.value as ContributionStatus,
                            })
                          }
                          className="rounded border border-neutral-800 bg-neutral-900/60 px-1.5 py-1 text-xs text-neutral-300"
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s.replace(/_/g, " ")}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Badge tone={STATUS_TONE[r.status]}>{r.status.replace(/_/g, " ")}</Badge>
                      )}
                    </td>
                    {canEdit && (
                      <td className="px-3 py-2.5 text-right">
                        <button
                          onClick={() => {
                            if (confirm("Delete this contribution record?")) remove.mutate(r.id);
                          }}
                          className="rounded p-1 text-neutral-500 hover:bg-neutral-800 hover:text-red-400"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
      {(setStatus.error ?? remove.error) && (
        <p className="text-xs text-red-300">{(setStatus.error ?? remove.error)?.message}</p>
      )}
    </>
  );
}

function AddContributionForm({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const [contributor, setContributor] = useState("");
  const [recipient, setRecipient] = useState("");
  const [office, setOffice] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [govEntity, setGovEntity] = useState("");
  const [amount, setAmount] = useState(0);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [canVote, setCanVote] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      createContribution({
        tenant_id: tenantId,
        contributor_name: contributor.trim(),
        recipient_name: recipient.trim(),
        office_sought: office.trim() || null,
        jurisdiction: jurisdiction.trim() || null,
        government_entity: govEntity.trim() || null,
        amount_usd: amount,
        contribution_date: date,
        can_vote_for_recipient: canVote,
      }),
    onSuccess: onDone,
  });

  const preview = assessPayToPlay(amount, canVote);
  const inputCls =
    "mt-1 w-full rounded-md border border-neutral-800 bg-neutral-900/60 px-2.5 py-1.5 text-sm text-neutral-200 placeholder:text-neutral-600";

  return (
    <Card className="space-y-3 p-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Contributor (covered associate)">
          <input
            className={inputCls}
            value={contributor}
            onChange={(e) => setContributor(e.target.value)}
          />
        </Field>
        <Field label="Recipient">
          <input
            className={inputCls}
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
          />
        </Field>
        <Field label="Office sought">
          <input
            className={inputCls}
            value={office}
            onChange={(e) => setOffice(e.target.value)}
            placeholder="e.g. State Treasurer"
          />
        </Field>
        <Field label="Jurisdiction">
          <input
            className={inputCls}
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value)}
            placeholder="e.g. California"
          />
        </Field>
        <Field label="Gov&rsquo;t-entity client (if any)">
          <input
            className={inputCls}
            value={govEntity}
            onChange={(e) => setGovEntity(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount (USD)">
            <input
              type="number"
              min={0}
              className={inputCls}
              value={amount}
              onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))}
            />
          </Field>
          <Field label="Date">
            <input
              type="date"
              className={inputCls}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs text-neutral-400">
        <input
          type="checkbox"
          checked={canVote}
          onChange={(e) => setCanVote(e.target.checked)}
          className="accent-brand-500"
        />
        Associate is entitled to vote for this recipient ({canVote ? "$350" : "$150"} de minimis
        applies)
      </label>
      {amount > 0 && (
        <div className="text-xs">
          {preview.exceeds ? (
            <span className="text-red-300">
              ⚠ {fmtUsd(amount)} exceeds the {fmtUsd(preview.limit)} de minimis — review for the
              two-year ban.
            </span>
          ) : (
            <span className="text-emerald-300">Within the {fmtUsd(preview.limit)} de minimis.</span>
          )}
        </div>
      )}
      {create.error && <p className="text-xs text-red-300">{create.error.message}</p>}
      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={!contributor.trim() || !recipient.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? <Spinner className="size-4" /> : <Plus className="size-4" />}
          Log contribution
        </Button>
      </div>
    </Card>
  );
}

export function ContributionsPage() {
  return (
    <TenantLayout>
      <ContributionsInner />
    </TenantLayout>
  );
}
